import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useState } from 'react';
import { VerifyEmailBanner } from '../routes/account';
import { LocationSwitcher } from './LocationSwitcher';
import { api, ApiError, authClient, type Me, type Salon } from '../lib/api';
import { TwoFactorSetup, useTwoFactorGate } from './TwoFactor';

// Loaded on first open, so the articles don't weigh on the app.
const HelpPanel = lazy(() => import('./HelpPanel'));
import { setLocale, useLocale, useT } from '../lib/i18n';
import { AgendaIcon, ClientsIcon, HelpIcon, PlusIcon, SalonIcon, ShieldIcon, TodayIcon } from './icons';
import { screenFromPath, type HelpScreen } from '../lib/help-parse';
import type { TKey } from '../lib/i18n';

const sections = [
  { to: '/s/$orgId/vandaag', key: 'nav.today', Icon: TodayIcon },
  { to: '/s/$orgId/agenda', key: 'nav.agenda', Icon: AgendaIcon },
  { to: '/s/$orgId/klanten', key: 'nav.clients', Icon: ClientsIcon },
  { to: '/s/$orgId/salon', key: 'nav.salon', Icon: SalonIcon },
] as const;

/** The KoalaSpot mark (a placeholder until the team's logo arrives): a serif "K" with a warm glow. */
function BrandMark() {
  return (
    <span className="relative flex size-11 items-center justify-center rounded-full bg-accent text-on-accent shadow-[0_0_28px_rgb(255_148_102/0.45)]">
      <span className="display -mt-0.5 text-[30px] leading-none">K</span>
    </span>
  );
}

/** Breadcrumb names in the top bar. */
const SCREEN_LABEL: Record<HelpScreen, TKey> = {
  vandaag: 'nav.today',
  agenda: 'nav.agenda',
  klanten: 'nav.clients',
  salon: 'nav.salon',
  welkom: 'welcome.title',
  account: 'acc.title',
  diensten: 'salon.services',
  team: 'salon.team',
  openingstijden: 'salon.hours',
  bedrijf: 'salon.business',
  'online-boeken': 'salon.booking',
  berichten: 'salon.messages',
  locaties: 'loc.title',
  privacy: 'salon.privacy',
};

export function AppLayout() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const t = useT();
  const locale = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me') });

  // From the account data, which stays put while queries refetch (a failed salon query would flicker).
  const membership = me.data?.salons.find((s) => s.orgId === orgId);
  const gate = useTwoFactorGate(
    (!!membership?.twoFactorRequired && !me.data?.user.twoFactorEnabled) || (salon.error instanceof ApiError && salon.error.code === 'two_factor_required' && !salon.isFetching),
  );

  const daysLeft = salon.data?.salon.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(salon.data.salon.trialEndsAt).getTime() - Date.now()) / 86_400_000))
    : null;

  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => {
    // "?" opens help anywhere, unless you're typing.
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key !== '?' || el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      e.preventDefault();
      setHelpOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const screen = screenFromPath(pathname);
  const initials = (me.data?.user.name ?? '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const trial = daysLeft !== null && salon.data?.salon.status === 'trialing';

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[92px_1fr] md:bg-frame md:bg-[radial-gradient(520px_420px_at_0%_0%,rgb(255_148_102/0.13),transparent_70%)]">
      {/* The rail on the desk: logo, the four sections in a floating pill, then help and you. */}
      <aside className="hidden md:sticky md:top-0 md:flex md:h-dvh md:flex-col md:items-center md:justify-between md:py-5">
        <Link to="/s/$orgId/vandaag" params={{ orgId }} aria-label={salon.data?.salon.name} className="group flex flex-col items-center gap-1.5">
          <BrandMark />
        </Link>
        <nav aria-label="Hoofdmenu" className="flex flex-col items-center gap-1 rounded-full bg-frame-soft/80 px-1.5 py-2.5 shadow-[inset_0_1px_0_rgb(255_255_255/0.06)]">
          {sections.map(({ to, key, Icon }) => (
            <Link
              key={to}
              to={to}
              params={{ orgId }}
              className="group relative flex w-[66px] flex-col items-center gap-1 rounded-[20px] py-2 text-[11px] font-medium text-canvas/55 hover:text-canvas"
              activeProps={{ 'aria-current': 'page', className: '!text-canvas [&>.ico]:bg-canvas [&>.ico]:text-ink [&>.ribbon]:opacity-100' }}
            >
              <span aria-hidden className="ribbon absolute -left-1.5 top-3.5 h-6 w-[3px] rounded-r-full bg-accent opacity-0 transition-opacity" />
              <span className="ico flex size-10 items-center justify-center rounded-full transition-colors group-hover:bg-white/[0.07]">
                <Icon />
              </span>
              {t(key)}
            </Link>
          ))}
        </nav>
        <div className="flex flex-col items-center gap-2">
          {me.data?.isAdmin && (
            <Link to="/beheer" aria-label={t('admin.link')} title={t('admin.link')} className="flex size-10 items-center justify-center rounded-full text-canvas/55 hover:bg-white/[0.07] hover:text-canvas">
              <ShieldIcon />
            </Link>
          )}
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-label={t('help.button')}
            title={`${t('help.button')} (?)`}
            aria-keyshortcuts="?"
            className="flex size-10 items-center justify-center rounded-full text-canvas/55 hover:bg-white/[0.07] hover:text-canvas"
          >
            <HelpIcon />
          </button>
          <Link
            to="/s/$orgId/account"
            params={{ orgId }}
            aria-label={t('acc.title')}
            title={me.data?.user.name}
            className="flex size-10 items-center justify-center rounded-full bg-frame-soft text-[12px] font-semibold text-canvas ring-1 ring-white/10 hover:bg-canvas hover:text-ink"
          >
            {initials}
          </Link>
        </div>
      </aside>

      <main className="min-w-0 bg-canvas px-4 pb-28 pt-2 md:my-2.5 md:mr-2.5 md:min-h-[calc(100dvh-20px)] md:rounded-[var(--radius-panel)] md:px-10 md:pb-14 md:pt-6">
        {/* One centred frame for the bar and the page, so they line up on every screen size. */}
        <div className="mx-auto w-full max-w-[1760px]">
        {/* Top of the page. Desktop: where you are, trial and location. Phone: the salon and help. */}
        <div className="relative z-10 mb-6 hidden min-h-9 items-center justify-between gap-4 md:flex">
          <p className="flex min-w-0 items-center gap-2 truncate text-sm text-muted">
            <span className="font-medium text-ink">{salon.data?.salon.name ?? ' '}</span>
            {screen && (
              <>
                <span aria-hidden className="text-muted/50">/</span>
                <span>{t(SCREEN_LABEL[screen])}</span>
              </>
            )}
          </p>
          <div className="flex items-center gap-3">
            {trial && (
              <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1 text-[13px] text-muted">
                <span aria-hidden className="size-1.5 rounded-full bg-accent" />
                {t('trial.daysLeft', { n: daysLeft! })}
              </span>
            )}
            <LocationSwitcher orgId={orgId} compact />
          </div>
        </div>
        <div className="-mx-1 mb-3 flex min-h-12 items-center justify-between gap-3 md:hidden">
          <p className="display truncate px-1 text-[24px]">{salon.data?.salon.name ?? ' '}</p>
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-label={t('help.button')}
            className="flex size-10 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-muted"
          >
            <HelpIcon />
          </button>
        </div>
        {salon.data?.supportUntil && <SupportBanner orgId={orgId} salonName={salon.data.salon.name} until={salon.data.supportUntil} />}
        {me.data && !me.data.user.emailVerified && <VerifyEmailBanner email={me.data.user.email} />}
        {salon.data && <AccountBanner orgId={orgId} salon={salon.data} />}
        {gate.show ? (
          <TwoFactorRequired message={t('tf.requiredOwner')} onActive={gate.onActive} />
        ) : (
          <>
            <LocationSwitcher orgId={orgId} className="mb-4 md:hidden" />
            <Outlet />
          </>
        )}
        </div>
      </main>

      {helpOpen && (
        <Suspense fallback={null}>
          <HelpPanel onClose={() => setHelpOpen(false)} />
        </Suspense>
      )}

      {/* Phone: the same floating dark pill as the rail, with the ribbon "+" in the middle. */}
      <nav
        aria-label="Hoofdmenu"
        className="fixed inset-x-3 z-40 grid grid-cols-5 items-center rounded-full bg-frame px-1.5 py-1.5 shadow-[0_10px_30px_rgb(29_25_21/0.28)] md:hidden"
        style={{ bottom: 'max(12px, env(safe-area-inset-bottom))' }}
      >
        {sections.slice(0, 2).map(({ to, key, Icon }) => (
          <BottomLink key={to} to={to} orgId={orgId} label={t(key)} Icon={Icon} />
        ))}
        <div className="flex items-center justify-center">
          <Link
            to="/s/$orgId/agenda"
            params={{ orgId }}
            search={{ nieuw: 1 }}
            aria-label={t('nav.new')}
            className="flex size-12 items-center justify-center rounded-full bg-accent text-on-accent shadow-[0_0_20px_rgb(255_148_102/0.45)]"
          >
            <PlusIcon />
          </Link>
        </div>
        {sections.slice(2).map(({ to, key, Icon }) => (
          <BottomLink key={to} to={to} orgId={orgId} label={t(key)} Icon={Icon} />
        ))}
      </nav>
    </div>
  );
}

/** Shown instead of the page when this account must use two-step verification first. */
export function TwoFactorRequired({ message, onActive }: { message: string; onActive: (active: boolean) => void }) {
  const t = useT();
  return (
    <section className="mx-auto flex max-w-2xl flex-col gap-4 rounded-[var(--radius-card)] border border-line bg-surface p-6">
      <h1 className="text-[22px] font-bold">{t('tf.title')}</h1>
      <p className="max-w-prose">{message}</p>
      <p className="max-w-prose text-sm text-muted">{t('tf.intro')}</p>
      <TwoFactorSetup enabled={false} required onActive={onActive} />
    </section>
  );
}

/** Always visible while our team looks in, so nobody forgets whose salon this is. */
/**
 * Where the account stands, when it needs attention (billing, plan 10): a trial ending within 3
 * days, a missed payment, or a paused subscription. An ended account always opens Abonnement
 * (Privacy and your own account stay reachable).
 */
function AccountBanner({ orgId, salon }: { orgId: string; salon: Salon }) {
  const t = useT();
  const navigate = useNavigate();
  const path = useRouterState({ select: (r) => r.location.pathname });
  const { status, trialEndsAt } = salon.salon;
  const allowed = /\/(salon\/abonnement|salon\/privacy|account)$/.test(path);
  useEffect(() => {
    if (status === 'expired' && !allowed) void navigate({ to: '/s/$orgId/salon/abonnement', params: { orgId }, replace: true });
  }, [status, allowed, orgId, navigate]);
  if (path.endsWith('/salon/abonnement')) return null;
  const days = trialEndsAt ? Math.ceil((Date.parse(trialEndsAt) - Date.now()) / 86_400_000) : null;
  const text =
    status === 'past_due' ? t('banner.pastDue')
    : status === 'suspended' ? t('banner.suspended')
    : status === 'trialing' && days !== null && days <= 3 && days >= 1 ? (days === 1 ? t('banner.trialEndsOne') : t('banner.trialEnds', { n: days }))
    : null;
  if (!text) return null;
  const tone = status === 'trialing' ? 'bg-accent-soft text-accent-strong' : status === 'past_due' ? 'bg-warning-soft text-warning' : 'bg-danger-soft text-danger';
  return (
    <div role="status" className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-control)] px-4 py-3 text-sm ${tone}`}>
      <p className="font-medium">{text}</p>
      {salon.role === 'owner' && (
        <Link to="/s/$orgId/salon/abonnement" params={{ orgId }} className="font-semibold underline">
          {t('banner.action')}
        </Link>
      )}
    </div>
  );
}

function SupportBanner({ orgId, salonName, until }: { orgId: string; salonName: string; until: string }) {
  const t = useT();
  const locale = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const end = useMutation({
    mutationFn: () => api(`/admin/salons/${orgId}/support`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['me'] });
      queryClient.removeQueries({ queryKey: ['salon', orgId] });
      navigate({ to: '/beheer' });
    },
  });
  const time = new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { hour: '2-digit', minute: '2-digit' }).format(new Date(until));
  return (
    <div role="status" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-control)] bg-warning-soft px-4 py-3 text-sm text-warning">
      <p className="font-medium">{t('support.banner', { salon: salonName, time })}</p>
      <button type="button" className="font-semibold underline" onClick={() => end.mutate()} disabled={end.isPending}>
        {t('support.end')}
      </button>
    </div>
  );
}

function BottomLink({
  to,
  orgId,
  label,
  Icon,
}: {
  to: (typeof sections)[number]['to'];
  orgId: string;
  label: string;
  Icon: typeof TodayIcon;
}) {
  return (
    <Link
      to={to}
      params={{ orgId }}
      className="flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-full text-[11px] font-medium text-canvas/60"
      activeProps={{ 'aria-current': 'page', className: '!text-ink bg-canvas' }}
    >
      <Icon />
      {label}
    </Link>
  );
}
